import React, { useEffect, useState } from "react";
import { Trash2, Speaker, Search } from "lucide-react";
import axios from "axios";

const FlashcardList = ({ token, flashcards, fetchFlashcards }) => {
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    if (token) fetchFlashcards();
    window.speechSynthesis.onvoiceschanged = () => {};
  }, [token, fetchFlashcards]);

  if (!token) {
    return (
      <div className="max-w-2xl mx-auto p-4 mt-6 text-green text-center font-medium">
        You must be logged in to view your flashcards.
      </div>
    );
  }

  // Text-to-speech
  const playText = (text, lang) => {
    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = lang;

    const voices = window.speechSynthesis.getVoices();
    const matchedVoice = voices.find((v) => v.lang.startsWith(lang));
    if (matchedVoice) {
      utterance.voice = matchedVoice;
    }

    window.speechSynthesis.speak(utterance);
  };

  // Delete flashcard and refetch
  const handleDelete = async (id) => {
    try {
      await axios.delete(`https://langbridge-backend.onrender.com/api/flashcards/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      fetchFlashcards();
    } catch (error) {
      console.error("Error deleting flashcard:", error);
    }
  };

  const filteredCards = flashcards.filter(
    (card) =>
      card.originalText.toLowerCase().includes(searchTerm.toLowerCase()) ||
      card.translatedText.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="max-w-4xl mx-auto p-4">
      <h2 className="text-2xl font-semibold mb-4 text-center text-blue">
        Your Flashcards
      </h2>

      <div className="relative w-full mb-6">
        <Search
          className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 pointer-events-none"
          size={18}
        />
        <input
          type="text"
          placeholder="Search flashcards..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-10 p-2 border border-purple rounded-md focus:outline-none focus:ring focus:border-blue"
        />
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        {filteredCards.length === 0 ? (
          <p className="text-gray-500">No flashcards found.</p>
        ) : (
          filteredCards.map((card) => (
            <div
              key={card._id}
              className="bg-gray shadow rounded-lg p-4 relative border border-purple"
            >
              <div className="absolute top-2 right-2">
                <button
                  onClick={() => handleDelete(card._id)}
                  className="text-red-500 hover:text-red-700"
                  title="Delete"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>

              <div className="mb-2">
                <h4 className="font-semibold text-purple">
                  Original ({card.sourceLang})
                </h4>
                <p className="text-gray-800">{card.originalText}</p>
                <button
                  onClick={() => playText(card.originalText, card.sourceLang)}
                  className="mt-1 text-black hover:text-purple"
                  title="Play Original"
                >
                  <Speaker className="w-5 h-5 inline" />
                </button>
              </div>

              <div>
                <h4 className="font-semibold text-purple">
                  Translation ({card.targetLang})
                </h4>
                <p className="text-gray-800">{card.translatedText}</p>
                <button
                  onClick={() => playText(card.translatedText, card.targetLang)}
                  className="mt-1 text-black hover:text-purple"
                  title="Play Translation"
                >
                  <Speaker className="w-5 h-5 inline" />
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default FlashcardList;
